import { useEffect, useState } from 'react';
import { Alert, Form, Button, Card } from 'react-bootstrap';
import axios from 'axios';
import WorkoutForm from './WorkoutForm.js';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://wellness.nextriking.com/api';
const API_URL = `${API_BASE_URL}/routines`;

const formatSeries = (value) => {
  try {
    const series = Array.isArray(value) ? value : JSON.parse(value || '[]');
    return series.length
      ? series.map(({ series: sets, peso, repeticiones }) => `${sets} x ${repeticiones} (${peso} kg)`).join(', ')
      : 'Sin series';
  } catch {
    return 'Sin series';
  }
};

const EditableSelect = ({
  label,
  category,
  controlId,
  options,
  selectedOption,
  onSelect,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const [newOption, setNewOption] = useState('');
  const [editedOptions, setEditedOptions] = useState({});

  useEffect(() => {
    setEditedOptions(Object.fromEntries(options.map((option) => [option.id, option.name])));
  }, [options]);

  const addOption = async () => {
    const name = newOption.trim();
    if (!name) return;

    await onAdd(name, category);
    setNewOption('');
  };

  const editOption = async (option) => {
    const name = (editedOptions[option.id] || '').trim();
    if (!name || name === option.name) return;

    await onEdit(option.id, name, category);
  };

  return (
    <Form.Group className="mb-3" controlId={controlId}>
      <Form.Label>{label}</Form.Label>
      <Form.Select
        value={selectedOption}
        onChange={(event) => onSelect(event.target.value)}
        aria-label={`Selecciona un ${label.toLowerCase()}`}
        required
      >
        <option value="">Selecciona...</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </Form.Select>

      <div className="mt-2">
        {options.map((option) => (
          <div className="d-flex gap-2 mb-2" key={`edit-${option.id}`}>
            <Form.Control
              type="text"
              value={editedOptions[option.id] ?? option.name}
              aria-label={`Editar ${option.name}`}
              onChange={(event) => setEditedOptions((current) => ({
                ...current,
                [option.id]: event.target.value,
              }))}
              onBlur={() => editOption(option)}
            />
            <Button
              type="button"
              variant="outline-danger"
              onClick={() => onDelete(option.id)}
            >
              Borrar
            </Button>
          </div>
        ))}
      </div>

      <div className="d-flex gap-2">
        <Form.Control
          type="text"
          placeholder={`Agregar ${label.toLowerCase()}`}
          value={newOption}
          onChange={(event) => setNewOption(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addOption();
            }
          }}
        />
        <Button type="button" variant="outline-primary" onClick={addOption}>
          Agregar
        </Button>
      </div>
    </Form.Group>
  );
};

const Routines = () => {
  const [users, setUsers] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedMuscleOption, setSelectedMuscleOption] = useState('');
  const [selectedMovementOption, setSelectedMovementOption] = useState('');
  const [selectedEquipmentOption, setSelectedEquipmentOption] = useState('');
  const [formFields, setFormFields] = useState([{ series: '', peso: '', repeticiones: '' }]);
  const [draftExercises, setDraftExercises] = useState([]);
  const [assignedTemplates, setAssignedTemplates] = useState([]);
  const [hasHealthAlert, setHasHealthAlert] = useState(false);
  const [success, setSuccess] = useState(null);

  const fetchData = async () => {
    try {
      const [usersResponse, routinesResponse, templatesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/users`),
        axios.get(API_URL),
        axios.get(`${API_URL}/templates`),
      ]);
      setUsers(Array.isArray(usersResponse.data.body) ? usersResponse.data.body : []);
      setRoutines(Array.isArray(routinesResponse.data.body) ? routinesResponse.data.body : []);
      setTemplates(Array.isArray(templatesResponse.data.body) ? templatesResponse.data.body : []);
    } catch (fetchError) {
      setError('No se pudieron cargar los alumnos o las opciones de rutinas.');
      console.error('Error al cargar datos de rutinas:', fetchError);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedUser) {
      setAssignedTemplates([]);
      setHasHealthAlert(false);
      return;
    }

    let cancelled = false;
    setHasHealthAlert(false);
    Promise.all([
      axios.get(`${API_URL}/assigned/user/${selectedUser}`),
      axios.get(`${API_BASE_URL}/health-records/user/${selectedUser}/alert`),
    ])
      .then(([routinesResponse, healthRecordResponse]) => {
        if (cancelled) return;
        setAssignedTemplates(Array.isArray(routinesResponse.data.body) ? routinesResponse.data.body : []);
        setHasHealthAlert(Boolean(healthRecordResponse.data.body?.hasHealthAlert));
      })
      .catch((fetchError) => {
        if (cancelled) return;
        setError('No se pudieron cargar las rutinas o la ficha médica del alumno.');
        console.error('Error al cargar la información del alumno:', fetchError);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedUser]);

  const saveRoutine = async (data) => {
    try {
      await axios.post(`${API_URL}/add`, data);
      await fetchData();
      setError(null);
    } catch (saveError) {
      setError('No se pudo guardar la opción.');
      console.error('Error al guardar rutina:', saveError);
    }
  };

  const addRoutine = (name, category) => saveRoutine({ id: 0, name, category });

  const editRoutine = (id, name, category) => saveRoutine({ id, name, category });

  const deleteRoutine = async (id) => {
    try {
      await axios.delete(`${API_URL}/delete`, { data: { id } });
      if (selectedMuscleOption === String(id)) setSelectedMuscleOption('');
      if (selectedMovementOption === String(id)) setSelectedMovementOption('');
      if (selectedEquipmentOption === String(id)) setSelectedEquipmentOption('');
      await fetchData();
      setError(null);
    } catch (deleteError) {
      setError('No se pudo borrar la opción.');
      console.error('Error al borrar rutina:', deleteError);
    }
  };

  const optionsByCategory = (category) => routines.filter(
    (routine) => Number(routine.category) === category
  );

  const addExerciseToTemplate = () => {
    if (!selectedMovementOption || !selectedMuscleOption || !selectedEquipmentOption) {
      setError('Selecciona ejercicio, músculo y equipamiento antes de agregarlo.');
      return;
    }
    if (formFields.some((field) => !field.series || !field.peso || !field.repeticiones)) {
      setError('Completa las series, el peso y las repeticiones antes de agregar el ejercicio.');
      return;
    }

    setDraftExercises((current) => [...current, {
      exerciseId: selectedMovementOption,
      muscleId: selectedMuscleOption,
      equipmentId: selectedEquipmentOption,
      series: formFields.map((field) => ({
        series: Number(field.series),
        peso: Number(field.peso),
        repeticiones: Number(field.repeticiones),
      })),
    }]);
    setSelectedMuscleOption('');
    setSelectedMovementOption('');
    setSelectedEquipmentOption('');
    setFormFields([{ series: '', peso: '', repeticiones: '' }]);
    setError(null);
  };

  const createTemplate = async (event) => {
    event.preventDefault();
    if (!title.trim() || !category.trim() || !draftExercises.length) {
      setError('Completa el título, la categoría y agrega al menos un ejercicio.');
      return;
    }

    try {
      await axios.post(`${API_URL}/templates`, {
        title: title.trim(),
        category: category.trim(),
        exercises: draftExercises,
      });
      setTitle('');
      setCategory('');
      setDraftExercises([]);
      await fetchData();
      setError(null);
      setSuccess('Plantilla creada correctamente.');
    } catch (saveError) {
      setSuccess(null);
      setError('No se pudo crear la plantilla.');
      console.error('Error al crear plantilla:', saveError);
    }
  };

  const assignTemplate = async (event) => {
    event.preventDefault();
    try {
      await axios.post(`${API_URL}/templates/${selectedTemplate}/assignments`, { userId: selectedUser });
      const response = await axios.get(`${API_URL}/assigned/user/${selectedUser}`);
      setAssignedTemplates(Array.isArray(response.data.body) ? response.data.body : []);
      setSelectedTemplate('');
      setError(null);
      setSuccess('Rutina asignada correctamente.');
    } catch (assignError) {
      setSuccess(null);
      setError('No se pudo asignar la rutina.');
      console.error('Error al asignar rutina:', assignError);
    }
  };

  const removeDraftExercise = (index) => setDraftExercises((current) => current.filter((_, exerciseIndex) => exerciseIndex !== index));

  const deleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API_URL}/templates/${templateId}`);
      await fetchData();
      setError(null);
    } catch (deleteError) {
      setError('No se pudo borrar la plantilla.');
      console.error('Error al borrar plantilla:', deleteError);
    }
  };

  const deleteAssignment = async (assignmentId) => {
    try {
      await axios.delete(`${API_URL}/assignments/${assignmentId}`);
      setAssignedTemplates((current) => current.filter((assignment) => assignment.id !== assignmentId));
      setError(null);
    } catch (deleteError) {
      setError('No se pudo quitar la rutina del alumno.');
      console.error('Error al quitar rutina:', deleteError);
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <Card style={{ width: '800px', padding: '20px' }}>
        <Card.Body>
          <Card.Title className="text-center">Plantillas de rutinas</Card.Title>
          {error && <p className="text-danger">{error}</p>}
          {success && <p className="text-success">{success}</p>}
          <Form onSubmit={createTemplate} noValidate>
            <Form.Group className="mb-3" controlId="routineTitle">
              <Form.Label>Título de rutina</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ej. Rutina de piernas"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="routineCategory">
              <Form.Label>Categoría</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ej. Piernas, empujes o tracciones"
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                required
              />
            </Form.Group>

            <EditableSelect
              label="Musculo"
              category={2}
              controlId="formBasicMuscle"
              options={optionsByCategory(2)}
              selectedOption={selectedMuscleOption}
              onSelect={setSelectedMuscleOption}
              onAdd={addRoutine}
              onEdit={editRoutine}
              onDelete={deleteRoutine}
            />
            <EditableSelect
              label="Movimiento / ejercicio"
              category={1}
              controlId="formBasicMovement"
              options={optionsByCategory(1)}
              selectedOption={selectedMovementOption}
              onSelect={setSelectedMovementOption}
              onAdd={addRoutine}
              onEdit={editRoutine}
              onDelete={deleteRoutine}
            />
            <EditableSelect
              label="Equipamiento"
              category={3}
              controlId="formBasicEquipment"
              options={optionsByCategory(3)}
              selectedOption={selectedEquipmentOption}
              onSelect={setSelectedEquipmentOption}
              onAdd={addRoutine}
              onEdit={editRoutine}
              onDelete={deleteRoutine}
            />

            <WorkoutForm formFields={formFields} onChange={setFormFields} />
            <Button variant="outline-primary" type="button" className="w-100 mb-3" onClick={addExerciseToTemplate}>
              Agregar ejercicio a la rutina
            </Button>
            {draftExercises.length > 0 && (
              <div className="mb-3">
                <h6>Ejercicios de la rutina</h6>
                {draftExercises.map((exercise, index) => {
                  const movement = routines.find((option) => String(option.id) === exercise.exerciseId);
                  const muscle = routines.find((option) => String(option.id) === exercise.muscleId);
                  const equipment = routines.find((option) => String(option.id) === exercise.equipmentId);
                  return (
                    <div className="d-flex justify-content-between align-items-center mb-2" key={`${exercise.exerciseId}-${index}`}>
                      <span>{movement?.name} - {muscle?.name} - {equipment?.name}: {formatSeries(exercise.series)}</span>
                      <Button type="button" variant="outline-danger" size="sm" onClick={() => removeDraftExercise(index)}>Quitar</Button>
                    </div>
                  );
                })}
              </div>
            )}
            <Button variant="primary" type="submit" className="w-100">Guardar plantilla</Button>
          </Form>
          <hr className="my-4" />
          <h5>Asignar plantilla</h5>
          <Form onSubmit={assignTemplate}>
            <Form.Group className="mb-3" controlId="formBasicUser">
              <Form.Label>Alumno</Form.Label>
              <Form.Select value={selectedUser} onChange={(event) => setSelectedUser(event.target.value)} required>
                <option value="">Selecciona...</option>
                {users.map((user) => {
                  const fullName = [user.name, user.lastname].filter(Boolean).join(' ');
                  return <option key={user.id} value={user.id}>{fullName || `Alumno ${user.id}`}</option>;
                })}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="templateAssignment">
              <Form.Label>Rutina</Form.Label>
              <Form.Select value={selectedTemplate} onChange={(event) => setSelectedTemplate(event.target.value)} required>
                <option value="">Selecciona...</option>
                {templates.map((template) => <option key={template.id} value={template.id}>{template.title} ({template.category})</option>)}
              </Form.Select>
            </Form.Group>
            {hasHealthAlert && (
              <Alert variant="warning" role="alert">
                Este alumno tiene información médica registrada. Consultá con tu entrenador antes de asignar o modificar una rutina.
              </Alert>
            )}
            <Button variant="success" type="submit" className="w-100">Asignar rutina</Button>
          </Form>
          {selectedUser && (
            <div className="mt-4">
              <h5>Rutinas asignadas</h5>
              {assignedTemplates.length ? (
                assignedTemplates.map((assignment) => (
                  <Card className="mb-2" key={assignment.id}>
                    <Card.Body>
                      <div className="d-flex justify-content-between">
                        <strong>{assignment.routine.title}</strong>
                        <Button type="button" variant="outline-danger" size="sm" onClick={() => deleteAssignment(assignment.id)}>Quitar</Button>
                      </div>
                      <div>{assignment.routine.category}</div>
                      {assignment.routine.exercises.map((exercise) => (
                        <div key={exercise.id}>{exercise.exercise.name} - {exercise.muscle.name} - {exercise.equipment.name}: {formatSeries(exercise.series)}</div>
                      ))}
                    </Card.Body>
                  </Card>
                ))
              ) : <p className="mb-0">Este alumno no tiene rutinas asignadas.</p>}
            </div>
          )}
          <hr className="my-4" />
          <h5>Plantillas guardadas</h5>
          {templates.map((template) => (
            <div className="d-flex justify-content-between align-items-center mb-2" key={template.id}>
              <span>{template.title} ({template.category})</span>
              <Button type="button" variant="outline-danger" size="sm" onClick={() => deleteTemplate(template.id)}>Borrar</Button>
            </div>
          ))}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Routines;
